import { Form, spinner } from '@/components';
import {
  Language,
  personalizationsHttp,
  ReportOrientationType,
  ReportSizeType,
} from '@/core';
import { openFileInNewWindow } from '@/utils';
import { useCategoryLookupSource } from '@/views';
import { Button, Col, Divider, Modal, Row } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import styles from './PersonalizationSummaryDownload.module.scss';
import {
  LANGUAGE_OPTIONS,
  ORIENTATION_OPTIONS,
  SIZE_OPTIONS,
} from './DownloadPersonalizationSummaryPdfModal';

interface Props {
  projectId: string;
  homeId: string;
  communityId: string;
  title: string;
  openButtonTitle: string;
  openButtonIcon?: React.ReactNode;
  downloadButtonTitle: string;
  downloadButtonIcon?: React.ReactNode;
  layout?: 'one-column' | 'two-columns';
}

const schema = Yup.object().shape({
  categoryIds: Yup.array().of(Yup.string().required()).nullable().defined(),
  size: Yup.mixed<ReportSizeType>().required(),
  orientation: Yup.mixed<ReportOrientationType>().required(),
  language: Yup.mixed<Language>().required(),
  showItemDescription: Yup.boolean().defined(),
  showConstructionDescription: Yup.boolean().defined(),
  showConstruction: Yup.boolean().defined(),
  showFloorplanImage: Yup.boolean().defined(),
  showInactive: Yup.boolean().defined(),
});

type PublicPersonalizationSummaryFormValue = OmitIndex<
  Yup.InferType<typeof schema>
>;

export function PublicDownloadPersonalizationSummaryPdfButton(props: Props) {
  const {
    projectId,
    homeId,
    communityId,
    title,
    openButtonTitle,
    openButtonIcon,
    downloadButtonTitle,
    downloadButtonIcon,
    layout = 'two-columns',
  } = props;
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const isTwoColumns = layout === 'two-columns';

  const categoriesDataSource = useCategoryLookupSource({
    communityId,
    listed: true,
    allowAnonymous: true,
  });

  const defaultLanguage = useMemo(
    () =>
      i18n.language?.toLowerCase().startsWith('es')
        ? Language.Spanish
        : Language.English,
    [i18n.language],
  );

  const initialValue = useMemo<PublicPersonalizationSummaryFormValue>(
    () => ({
      categoryIds: [],
      size: ReportSizeType.Letter,
      orientation: ReportOrientationType.Portrait,
      language: defaultLanguage,
      showItemDescription: true,
      showConstructionDescription: false,
      showConstruction: false,
      showFloorplanImage: true,
      showInactive: true,
    }),
    [defaultLanguage],
  );

  return (
    <>
      <Button icon={openButtonIcon} onClick={() => setOpen(true)}>
        {openButtonTitle}
      </Button>

      <Modal
        visible={open}
        title={title}
        width={isTwoColumns ? 1000 : 600}
        onCancel={() => setOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setOpen(false)}>
            {t('cancel')}
          </Button>,
          <Form.Submit
            key="download"
            uid="download-public-personalization-summary-pdf"
            type="primary"
            icon={downloadButtonIcon}
          >
            {downloadButtonTitle}
          </Form.Submit>,
        ]}
      >
        <Form.Formik<PublicPersonalizationSummaryFormValue>
          uid="download-public-personalization-summary-pdf"
          i18n="personalization.downloadPdf"
          initialValues={initialValue}
          validationSchema={schema}
          onSubmit={(values) => {
            spinner.show();
            const request = {
              projectId,
              homeId,
              language: values.language,
              showConstruction: values.showConstruction,
              showItemDescription: values.showItemDescription,
              showConstructionDescription: values.showConstructionDescription,
              categoryIds:
                values.categoryIds != null && values.categoryIds.length > 0
                  ? values.categoryIds
                  : null,
              showFloorplanImage: values.showFloorplanImage,
              showInactiveItems: values.showInactive,
            };

            const download =
              values.size === ReportSizeType.Tabloid
                ? personalizationsHttp.createPublicPersonalizationSummaryTabloidPdf(
                    {
                      ...request,
                      showResidentInfo: false,
                    },
                  )
                : personalizationsHttp.createPublicPersonalizationSummaryPdf({
                    ...request,
                    orientation: values.orientation,
                    showBaseCost: false,
                    showResidentPrice: false,
                    showResidentInfo: false,
                    showSignature: false,
                  });

            download
              .then((file) => {
                openFileInNewWindow(file);
                setOpen(false);
              })
              .finally(() => {
                spinner.hide();
              });
          }}
          enableReinitialize
        >
          {({ values }) => (
            <Row>
              <Col span={isTwoColumns ? 12 : 24}>
                <Form.AsyncSelect
                  dataSource={categoriesDataSource}
                  name="categoryIds"
                  mode="multiple"
                  preload
                />

                <Divider />
                <Form.Radio name="size" options={SIZE_OPTIONS} inline={false} />
                <Divider />

                {values.size === ReportSizeType.Letter && (
                  <>
                    <Form.Radio
                      name="orientation"
                      options={ORIENTATION_OPTIONS}
                      inline={false}
                    />
                    <Divider />
                  </>
                )}

                <Form.Radio
                  name="language"
                  options={LANGUAGE_OPTIONS}
                  inline={false}
                />
                <Divider />

                <Form.Group
                  name="showConstructionDescription"
                  label={t('personalization.downloadPdf.description')}
                  inline={false}
                >
                  <Form.Checkbox
                    name="showItemDescription"
                    label={t(
                      'personalization.downloadPdf.descriptionOptions.item',
                    )}
                  />
                  <Form.Checkbox
                    name="showConstructionDescription"
                    label={t(
                      'personalization.downloadPdf.descriptionOptions.construction',
                    )}
                  />
                </Form.Group>
              </Col>

              {!isTwoColumns && (
                <Col span={24}>
                  <Divider />
                  <PublicRightColumnContent />
                </Col>
              )}

              {isTwoColumns && (
                <Col span={12} className={styles.secondColumn}>
                  <PublicRightColumnContent />
                </Col>
              )}
            </Row>
          )}
        </Form.Formik>
      </Modal>
    </>
  );
}

function PublicRightColumnContent() {
  const { t } = useTranslation();

  return (
    <>
      <Form.Checkbox
        name="showConstruction"
        label={t('personalization.downloadPdf.showConstruction')}
      />
      <Form.Checkbox
        name="showFloorplanImage"
        label={t('personalization.summary.showFloorplanImage')}
      />
      <Form.Checkbox
        name="showInactive"
        label={t('personalization.summary.showInactive')}
      />
    </>
  );
}


